import {
  faArrowLeft,
  faKey,
  faSave,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import MultiSelect from 'components/common/MultiSelect';
import Editor from 'components/editor/Editor';
import Loading from 'components/loading/Loading';
import DialogModal from 'components/modals/DialogModal';
import useApi from 'helpers/api';
import { processHtmlForEmail } from 'helpers/utils';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Nav, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import MaskedInput from 'react-text-mask';
import AgentServiceGroupService from 'services/AgentServiceGroup.service';
import AgentsService from 'services/Agents.service';
import ProfilesService from 'services/Profiles.service';
import ServiceGroupService from 'services/ServiceGroup.service';

const FormActions = ({
  isEdit,
  handleNavigateBack,
  handleResetPassword,
  handleSubmit,
  showTitle = true
}) => (
  <Row as={Flex} alignItems="center" justifyContent="between" className="w-100">
    <Col xs="auto">
      {showTitle && (
        <h5 className="mb-0">{isEdit ? 'Editar Usuário' : 'Novo Usuário'}</h5>
      )}
    </Col>
    <Col xs="auto" as={Flex} alignItems="center">
      <Button
        variant="falcon-default"
        size="sm"
        onClick={handleNavigateBack}
        className="me-2"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
        Voltar
      </Button>
      {isEdit && (
        <Button
          variant="falcon-default"
          size="sm"
          className="me-2"
          onClick={handleResetPassword}
        >
          <FontAwesomeIcon icon={faKey} className="me-1" />
          Resetar Senha
        </Button>
      )}
      <Button variant="primary" size="sm" onClick={handleSubmit}>
        <FontAwesomeIcon icon={faSave} className="me-1" />
        Salvar
      </Button>
    </Col>
  </Row>
);

const AgentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const isEdit = !!id;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [validated, setValidated] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [showDialogConfirm, setShowDialogConfirm] = useState(false);

  const form = useRef(null);

  const initialState = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    inactive: false,
    profile: { id: null },
    serviceGroupsIds: [],
    canAccessAllCustomers: true,
    customersIds: [],
    signature: ''
  };

  const [body, setBody] = useState(initialState);
  const [profiles, setProfiles] = useState([]);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [customers, setCustomers] = useState({ content: [] });

  // State for selections
  const [selectedServiceGroups, setSelectedServiceGroups] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  useEffect(() => {
    const loadDependencies = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          getProfiles(),
          getServiceGroups(),
          fetchCustomers()
        ]);

        if (isEdit) {
          await loadAgentData();
        }
      } catch (error) {
        console.error('Error loading data:', error);
        showErrorNotification('Erro ao carregar dados');
      } finally {
        setIsLoading(false);
      }
    };
    loadDependencies();
  }, [id]);

  // Effect to sync selectedServiceGroups with body.serviceGroupsIds
  useEffect(() => {
    setBody(prev => ({
      ...prev,
      serviceGroupsIds: selectedServiceGroups.map(sg => sg.value)
    }));
  }, [selectedServiceGroups]);

  // Effect to sync selectedCustomers with body.customersIds
  useEffect(() => {
    setBody(prev => ({
      ...prev,
      customersIds: selectedCustomers.map(c => c.value)
    }));
  }, [selectedCustomers]);

  const getProfiles = async () => {
    let response = await ProfilesService.list();
    if (response.content) setProfiles(response.content);
  };

  const getServiceGroups = async () => {
    const response = await ServiceGroupService.list();
    const formattedGroups = response.content.map(group => ({
      value: group.id,
      label: group.name
    }));
    setServiceGroups(formattedGroups);
  };

  const fetchCustomers = async () => {
    const queryParams = new URLSearchParams({
      page: 0,
      size: 1000,
      includeInactives: false
    }).toString();
    const response = await api.customers.list(queryParams);
    setCustomers(response);
  };

  const loadAgentData = async () => {
    try {
      const [response, signatureResponse] = await Promise.all([
        api.agents.findById(id),
        AgentsService.getSignature(id)
      ]);

      const agentData = response;
      const signature = signatureResponse.signature;

      // Populate form
      setBody({
        ...agentData,
        firstName: agentData.firstName,
        lastName: agentData.lastName,
        email: agentData.email,
        phone: agentData.phone,
        inactive: agentData.inactive,
        profile: agentData.profile || { id: null },
        canAccessAllCustomers: agentData.canAccessAllCustomers,
        signature: signature || '',
        serviceGroupsIds: [], // Will be set by loading associated groups
        customersIds: [] // Will be set by associated customers
      });

      // Set Selected Customers
      if (agentData.associatedCustomers) {
        setSelectedCustomers(
          agentData.associatedCustomers.map(customer => ({
            value: customer.id,
            label: customer.name || customer.fantasyName
          }))
        );
      }

      // Load associated service groups
      await getAssociatedServiceGroups(id);
    } catch (error) {
      console.error('Error loading agent:', error);
      showErrorNotification('Erro ao carregar dados do usuário');
    }
  };

  const getAssociatedServiceGroups = async agentId => {
    setIsLoadingGroups(true);
    try {
      const response = await AgentServiceGroupService.list(agentId);
      if (response && Array.isArray(response)) {
        const formattedGroups = response.map(group => ({
          value: group.id,
          label: group.name
        }));
        setSelectedServiceGroups(formattedGroups);
      }
    } catch (error) {
      console.error('Error loading service groups:', error);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.current.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      showErrorNotification('Por favor, preencha os campos obrigatórios');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        firstName: body.firstName,
        lastName: body.lastName,
        email: body.email,
        phone: body.phone ? body.phone : null,
        profile: body.profile,
        inactive: body.inactive,
        serviceGroupsIds: body.serviceGroupsIds,
        canAccessAllCustomers: body.canAccessAllCustomers,
        customersIds: body.customersIds,
        signature: body.signature ? processHtmlForEmail(body.signature) : null
      };

      let response;
      if (isEdit) {
        response = await api.agents.edit(id, payload);
      } else {
        response = await api.agents.create(payload);
      }

      showSuccessNotification('Usuário salvo com sucesso');
      navigate(-1);
    } catch (error) {
      console.error('Error saving agent:', error);
      showErrorNotification('Erro ao salvar usuário');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async confirm => {
    if (!confirm) {
      setShowDialogConfirm(false);
      return;
    }

    setIsLoading(true);
    try {
      const req = await api.agents.resetPassword(id);
      if (req.ok) {
        showSuccessNotification(
          'Senha resetada com sucesso. A nova senha será enviada no e-mail do agente.'
        );
      } else {
        showErrorNotification(req.userMessage || 'Erro ao resetar senha');
      }
    } catch (error) {
      showErrorNotification('Erro ao resetar senha');
    } finally {
      setShowDialogConfirm(false);
      setIsLoading(false);
    }
  };

  const fullName = `${body.firstName} ${body.lastName || ''}`;

  const navItems = [
    { id: 'basic', label: 'Informações básicas' },
    { id: 'serviceGroups', label: 'Grupos de serviços' },
    { id: 'customers', label: 'Clientes' },
    { id: 'profile', label: 'Perfil de Permissões' },
    { id: 'signature', label: 'Assinatura' }
  ];

  const handleNavClick = id => {
    setActiveTab(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {isLoading && <Loading />}
      <Card className="mb-3">
        <Card.Header>
          <FormActions
            isEdit={isEdit}
            handleNavigateBack={() => navigate(-1)}
            handleResetPassword={() => setShowDialogConfirm(true)}
            handleSubmit={handleSubmit}
          />
        </Card.Header>
        <Card.Body>
          <Form validated={validated} ref={form} onSubmit={handleSubmit}>
            <Row>
              <Col md={3}>
                <Nav
                  className="flex-column nav-pills sticky-top"
                  style={{ top: '80px' }}
                >
                  {navItems.map(item => (
                    <div
                      key={item.id}
                      active={activeTab === item.id}
                      onClick={() => handleNavClick(item.id)}
                      className={`mb-2 p-2 text-body-secondary ${
                        activeTab === item.id
                          ? 'text-primary btn-falcon-default rounded'
                          : ''
                      }`}
                      style={{ cursor: 'pointer' }}
                    >
                      {item.label}
                    </div>
                  ))}
                </Nav>
              </Col>

              <Col md={9}>
                <div id="basic" className="mb-5 pt-2">
                  <h5 className="mb-3">Informações básicas</h5>
                  <Row className="mb-3">
                    <Form.Group as={Col} md="6" controlId="firstName">
                      <Form.Label>
                        Nome <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="text"
                        placeholder="Nome"
                        value={body.firstName}
                        onChange={e =>
                          setBody({ ...body, firstName: e.target.value })
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        Insira um nome.
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} md="6" controlId="lastName">
                      <Form.Label>
                        Sobrenome <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="text"
                        placeholder="Sobrenome"
                        value={body.lastName}
                        onChange={e =>
                          setBody({ ...body, lastName: e.target.value })
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        Insira um sobrenome.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group as={Col} md="6" controlId="email">
                      <Form.Label>
                        E-mail <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="email"
                        placeholder="E-mail"
                        value={body.email}
                        onChange={e =>
                          setBody({ ...body, email: e.target.value })
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        Insira um e-mail.
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} md="6" controlId="phone">
                      <Form.Label>Telefone</Form.Label>
                      <MaskedInput
                        mask={[
                          '(',
                          /[1-9]/,
                          /\d/,
                          ')',
                          ' ',
                          /\d/,
                          /\d/,
                          /\d/,
                          /\d/,
                          /\d/,
                          '-',
                          /\d/,
                          /\d/,
                          /\d/,
                          /\d/
                        ]}
                        placeholder="Telefone"
                        guide={false}
                        value={body.phone}
                        onChange={e =>
                          setBody({ ...body, phone: e.target.value })
                        }
                        render={(ref, props) => (
                          <Form.Control ref={ref} {...props} />
                        )}
                      />
                    </Form.Group>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="inactive-switch"
                      label="Usuário inativo"
                      checked={body.inactive}
                      onChange={e =>
                        setBody({ ...body, inactive: e.target.checked })
                      }
                    />
                  </Form.Group>
                </div>

                <div id="serviceGroups" className="mb-5 pt-5 border-top">
                  <h5 className="mb-3">Grupos de serviços</h5>
                  <Form.Group controlId="serviceGroupsInput">
                    <Form.Label>Selecione os grupos de serviço</Form.Label>
                    <MultiSelect
                      placeholder={
                        isLoadingGroups
                          ? 'Carregando...'
                          : 'Selecione os grupos...'
                      }
                      options={serviceGroups}
                      value={selectedServiceGroups}
                      onChange={setSelectedServiceGroups}
                    />
                  </Form.Group>
                </div>

                <div id="customers" className="mb-5 pt-5 border-top">
                  <h5 className="mb-3">Clientes</h5>
                  <div className="mb-3">
                    <Form.Label className="mb-2">
                      Defina quais clientes o agente pode acessar
                    </Form.Label>
                    <div className="border rounded p-3 mb-3">
                      <Flex
                        alignItems="center"
                        className="mb-2"
                        justifyContent={'between'}
                      >
                        <div>
                          <FontAwesomeIcon
                            icon={faUser}
                            className="me-2 text-primary"
                          />
                          <span className="me-2">Todos os clientes</span>
                        </div>
                        <Form.Check
                          type="switch"
                          checked={body.canAccessAllCustomers}
                          onChange={e =>
                            setBody({
                              ...body,
                              canAccessAllCustomers: e.target.checked
                            })
                          }
                          className="fw-semibold"
                        />
                      </Flex>
                      {body.canAccessAllCustomers && (
                        <p className="mb-0 ms-4">
                          Este agente terá acesso a todos os clientes
                        </p>
                      )}
                    </div>

                    {!body.canAccessAllCustomers && (
                      <div className="border rounded p-3">
                        <p>Clientes Disponíveis</p>
                        <div className="p-2">
                          <MultiSelect
                            placeholder="Selecione os clientes..."
                            options={customers.content.map(c => ({
                              value: c.id,
                              label: c.name || c.fantasyName
                            }))}
                            value={selectedCustomers}
                            onChange={setSelectedCustomers}
                            isSearchable
                            isMulti
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div id="profile" className="mb-5 pt-5 border-top">
                  <h5 className="mb-3">Perfil de Permissões</h5>
                  <Form.Group controlId="profileSelect">
                    <Form.Label>
                      Perfil de agente <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      required
                      value={body.profile?.id || ''}
                      onChange={e =>
                        setBody({ ...body, profile: { id: e.target.value } })
                      }
                    >
                      <option value="">Selecione uma opção</option>
                      {profiles.map((item, i) => (
                        <option value={item.id} key={i}>
                          {item.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Escolha uma opção!
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>

                <div id="signature" className="mb-5 pt-5 border-top">
                  <h5 className="mb-3">Assinatura</h5>
                  <Form.Group controlId="signatureInput">
                    <Form.Label>Assinatura do E-mail</Form.Label>
                    <Editor
                      data={body.signature}
                      value={value => setBody({ ...body, signature: value })}
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
        <Card.Footer>
          <FormActions
            isEdit={isEdit}
            handleNavigateBack={() => navigate(-1)}
            handleResetPassword={() => setShowDialogConfirm(true)}
            handleSubmit={handleSubmit}
            showTitle={false}
          />
        </Card.Footer>
      </Card>

      <DialogModal
        message={
          <Flex direction={'column'}>
            <span>
              Deseja realmente resetar a senha de {fullName}? A senha anterior
              será invalidada imediatamente.
            </span>
            <small className="text-muted">
              A nova senha será enviada por e-mail ao agente.
            </small>
          </Flex>
        }
        showModal={showDialogConfirm}
        handleClose={() => setShowDialogConfirm(false)}
        response={handleResetPassword}
      />
    </>
  );
};

export default AgentForm;
